/* eslint-disable react-hooks/exhaustive-deps */
import { BaseTable, Card, FooterMenu } from "components"
import moment from "moment"
import { Fragment, useEffect, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useApi, useHandleAuth, usePermission, useToggle } from "utils"
import { thisMonth } from "utils/functions"
import {
  BalanceSheetNoneType,
  BalanceSheetReportType,
  DateType,
  FilterReportPNL,
  ProfitLossComparisonType,
  ResponsePNL
} from "../../types"
import { periode } from "../../utils/vars"
import { Item } from "../TabBalanceSheet/components"
import { ItemTotal } from "../TabBalanceSheet/components/ItemTotal"
import { calculateParent } from "../TabBalanceSheet/utils/functions"
import { DataStatusProfitNLoss, FilterPL, HeaderCOA, HeaderProfitNLoss } from "./components"
import {
  generateNetProfit,
  handleExportExcelProfitLoss
} from "./utils/functions"
import { useExportPdf } from "./utils/hooks"
// import { useGestSingleSettingCompany } from "pages/Setup/Preference/SettingCompany/utils"
import { CoaHeaderEnum } from "pages/Accounts/Report/BugetManager/enum"
import { CoaEnum } from "../../enums"
import { useCollapseState } from "../../utils"

export const TabProfitLoss = () => {
  const permission = usePermission(["AP02"])
  const api = useApi()
  const auth = useHandleAuth()
  const token = auth.userTokenData()
  const toggleAccountNumber = useToggle(true)
  const toggleZeroValue = useToggle(true)
  const toggleSubHeader = useToggle(true)
  const toggleDetail = useToggle(true)
  // const dataSettingCompany = useGestSingleSettingCompany()

  const [isLoading, setIsLoading] = useState(false)
  const [comparisonDate, setComparisonDate] = useState<string[]>([])
  const [reportData, setReportData] = useState<Array<BalanceSheetReportType>>([])

  const nonZeroReportData: BalanceSheetReportType[] = reportData.filter(
    (item) => item.balance.some((balance) => balance !== 0)
  )
  const data = toggleZeroValue.isActive ? reportData : nonZeroReportData
  const dataWithOutNetProfit = data.filter((item) => item.coa_name !== CoaEnum.NET_PROFIT)

  const exportPdf = useExportPdf({
    data: data,
    comparisonDates: comparisonDate
  })

  const dateTypePNL = localStorage?.getItem("dateTypePNL")
  const DateStartPNL = localStorage?.getItem("DateStartPNL")
  const DateEndPNL = localStorage?.getItem("DateEndPNL")

  const methods = useForm<FilterReportPNL>({
    defaultValues: {
      dateType: dateTypePNL ? dateTypePNL as DateType : "1",
      date: DateStartPNL ? DateStartPNL : thisMonth().fromDate,
      dateTo: DateEndPNL ? DateEndPNL : thisMonth().toDate,
      periode: periode[0],
      comparison: undefined,
      filter: "0"
    }
  })
  const isFetching = methods.formState.isSubmitting

  const [date, dateTo] = methods.watch(["date", "dateTo"])
 
  const onSubmitFilter = async (data: FilterReportPNL) => {
    setComparisonDate([])
    setIsLoading(true)

    const isFilterAll = Boolean(data.filter === "0")

    await api
      .postForm<{ payload: ResponsePNL }>("/profitandlost/", {
        DateType: data.dateType,
        DateStart: data.date,
        DateEnd: data.dateTo,
        PeriodType: data.periode.value,
        Comparison: data.comparison
      })
      .then((res) => {
        /**
         * Should pay attention to the response type, the response has 2 different type.
         * if the comparation is exist, the response will be an array of array and need to be flatten for simplicity
         * else, if the comparation is undefined, the response will be an array of object
         **/

        setIsLoading(false)

        if (data.comparison) {
          const payload = res.data.payload as ProfitLossComparisonType[][]

          const flattenPayload = payload.flat()
          const dateCompare = flattenPayload.map(({ DateStart }) => `${moment(DateStart).format("MMM YYYY")}`)

          const calculate = flattenPayload.map((data) => {
            return {
              ...data,
              BSData: calculateParent(data.BSData)
            }
          })

          const filterByCoaGrup = isFilterAll
            ? calculate.map((row) => ({ ...row, BSData: generateNetProfit(row.BSData) }))
            : calculate.map((row) => ({ ...row, BSData: row.BSData.filter((coa) => coa.group_coa_code === data.filter )}))

          const mapBalanceToArray = filterByCoaGrup[0].BSData?.map((data) => ({
            ...data,
            balance: filterByCoaGrup.map(({ BSData }) => {
              return (BSData?.find(({ coa_id }) => coa_id === data.coa_id)?.balance || 0)
            })
          }))

          setComparisonDate(dateCompare)
          setReportData(mapBalanceToArray)
        } else {
          const date = moment(data.date)
          const dateTo = moment(data.dateTo)

          const payload = res.data.payload as BalanceSheetNoneType[]
          const dateCompare = [`${date.format("DD/MM/YYYY")} - ${dateTo.format("DD/MM/YYYY")}`]
          const calculate = calculateParent(payload)

          const filterByCoaGrup = isFilterAll ? generateNetProfit(calculate) : calculate.filter((coa) => coa.group_coa_code === data.filter)
          const mapBalanceToArray = filterByCoaGrup.map((data) => ({ ...data, balance: [data.balance] }))

          setComparisonDate(dateCompare)
          setReportData(mapBalanceToArray)
        }
      })
  }

  const handleExport = () => {
    handleExportExcelProfitLoss({
      comparisonDates: comparisonDate,
      data,
      companyName: token?.company_name ?? "",
      date: methods.getValues().date,
      endDate: methods.getValues().dateTo,
    })
  }

  useEffect(() => {
    if (methods.formState.submitCount === 0) {
      methods.handleSubmit((data) => onSubmitFilter(data))()
    }
  }, [])

  const totalGrossProfit = () => {
    const totalIncome: number[] = data?.find((item) => item.coa_name?.toUpperCase() === CoaEnum.INCOME)?.balance ?? [];
    const totalCOGS: number[] = data?.find((item) => item.coa_name?.toUpperCase() === CoaEnum.COST_OF_SALES)?.balance ?? [];
  
    const grossProfit: number[] = totalIncome?.map((income, index) => income - totalCOGS[index]);
    const summary = grossProfit?.reduce((acc, curr) => acc + curr, 0);
    const average = summary / grossProfit?.length;

    return { grossProfit, summary, average }
  }
  const totalEbitda = () => {
    const totalExpenses: number[] = data?.find((item) => item.coa_name?.toUpperCase() === CoaEnum.EXPENSES)?.balance ?? [];

    const ebitda: number[] = totalGrossProfit()?.grossProfit?.map((profit, index) => {
      const expense = totalExpenses[index] ?? 0;
      return profit - expense;
    });

    const summary = ebitda?.reduce((acc, curr) => acc + curr, 0) ?? 0;
    const average = ebitda && ebitda.length > 0 ? summary / ebitda.length : 0;

    return { ebitda, summary, average };
  };
  const totalEbit = () => {
    const ebitda: number[] = totalEbitda()?.ebitda ?? [];
    const totalOtherIncome: number[] = data?.find((item) => item.coa_name?.toUpperCase() === CoaEnum.OTHER_INCOME)?.balance ?? [];
    const totalOtherExpenses: number[] = data?.find((item) => item.coa_name?.toUpperCase() === CoaEnum.OTHER_EXPENSES)?.balance ?? [];

    const maxLength = Math.max(
      ebitda.length,
      totalOtherIncome.length,
      totalOtherExpenses.length
    );

    const ebit: number[] = Array.from({ length: maxLength }, (_, index) => {
      const ebitdaValue = ebitda[index] ?? 0;
      const otherIncome = totalOtherIncome[index] ?? 0;
      const otherExpense = totalOtherExpenses[index] ?? 0;

      return (otherIncome + ebitdaValue) - (otherExpense);
    });

    const summary = ebit.reduce((acc, curr) => acc + curr, 0);
    const average = ebit.length > 0 ? summary / ebit.length : 0;

    return { ebit, summary, average };
  };
  // const totalTax = () => {
  //   const baseEbit: number[] = totalEbit()?.ebit ?? []

  //   const tax: number[] = baseEbit.map((val) => (val ?? 0) * (dataSettingCompany?.data?.[0]?.tax ?? 0) || 0)
  //   const summary = tax?.reduce((acc, curr) => acc + curr, 0)
  //   const average = summary / (tax?.length)

  //   return { tax, summary, average }
  // }
  const totalNetProfit = () => {
    const ebit: number[] = totalEbit()?.ebit ?? [];
    const totalIncomeTaxExpenses: number[] = data?.find((item) => item.coa_name?.toUpperCase() === CoaEnum.INCOME_TAX_EXPENSES)?.balance ?? [];

    const netProfit: number[] = ebit.map((val, index) => {
      const taxExpense = totalIncomeTaxExpenses[index] ?? 0;
      return val - taxExpense;
    });

    const summary = netProfit.reduce((acc, curr) => acc + curr, 0);
    const average = netProfit.length > 0 ? summary / netProfit.length : 0;

    return { netProfit, summary, average };
  };

  const { isCollapsed, toggleCollapse } = useCollapseState(dataWithOutNetProfit, "coaPNL")

  return (
    <Fragment>
      <section className="mt-2">
        <Card>
          <Card.Body>
            <FormProvider {...methods}>
              <FilterPL
                permission={permission?.canUpdate}
                toggleAccountNumber={toggleAccountNumber}
                toggleZeroValue={toggleZeroValue}
                toggleSubHeader={toggleSubHeader}
                toggleDetail={toggleDetail}
                onSubmit={(data) => {
                  onSubmitFilter(data)
                  localStorage.setItem("dateTypePNL", data?.dateType as DateType)
                  localStorage.setItem("DateStartPNL", data?.date as DateType)
                  localStorage.setItem("DateEndPNL", data?.dateTo as DateType)
                }}
              />
            </FormProvider>
          </Card.Body>
        </Card>
      </section>

      
      <section className="mt-2">
        <Card className="mb-2">
          <HeaderProfitNLoss label="Profit And Loss" startDate={date} endDate={dateTo} />
          <Card.Body className="overflow-x-auto max-h-[calc(100vh-640px)]">
            <div className="overflow-x-auto">
              <BaseTable className="border-spacing-0"> 
                <thead>
                  <tr className="uppercase">
                    <th></th>
                    {comparisonDate?.map((date) => (
                      <th className="text-right" key={date}>
                        {date}
                      </th>
                    ))}
                    <th>SUMMARY</th>
                    <th>AVERAGE</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading 
                    ? <DataStatusProfitNLoss loading={isLoading} /> 
                    : !reportData?.length || isFetching 
                    ? <DataStatusProfitNLoss loading={isFetching} />
                    : (
                        <>
                          {dataWithOutNetProfit.map((item, _key: number) => item.parent_id === -1 && (
                            <>
                              <Item
                                isPNL
                                comparisonDates={comparisonDate}
                                key={item.coa_id}
                                list={dataWithOutNetProfit}
                                item={item}
                                displayAccountNumber={toggleAccountNumber.isActive}
                                displaySubHeader={toggleSubHeader.isActive}
                                displayDetail={toggleDetail.isActive}
                                isCollapsed={isCollapsed}
                                toggleCollapse={toggleCollapse}
                                date={date}
                                dateTo={dateTo}
                              />

                              {item.coa_name === "NET PROFIT" ? null : (<ItemTotal isPNL item={item} />)}

                              <HeaderCOA 
                                coa_name={item?.coa_name}
                                coa_enum={CoaEnum.COST_OF_SALES}
                                coa_level={item?.coa_level}
                                label="GROSS PROFIT"
                                balance={totalGrossProfit()?.grossProfit}
                                header_level={item?.header_level}
                                summary={totalGrossProfit()?.summary}
                                average={totalGrossProfit()?.average}
                              />

                              <HeaderCOA 
                                coa_name={item?.coa_name}
                                coa_enum={CoaEnum.EXPENSES}
                                coa_level={item?.coa_level}
                                label="EBITDA"
                                balance={totalEbitda()?.ebitda}
                                header_level={item?.header_level}
                                summary={totalEbitda()?.summary}
                                average={totalEbitda()?.average}
                              />

                              <HeaderCOA 
                                coa_name={item?.coa_name}
                                coa_enum={CoaEnum.OTHER_EXPENSES}
                                coa_level={item?.coa_level}
                                label="EBIT"
                                balance={totalEbit()?.ebit}
                                header_level={item?.header_level}
                                summary={totalEbit()?.summary}
                                average={totalEbit()?.average}
                              />

                              {/* <HeaderCOA
                                coa_name={item?.coa_name}
                                coa_enum={CoaEnum.INCOME_TAX}
                                coa_level={item?.coa_level}
                                label="TAX ( 20% )"
                                balance={totalTax()?.tax}
                                header_level={item?.header_level}
                                summary={totalTax()?.summary}
                                average={totalTax()?.average}
                              /> */}
                              
                            </>
                          ))}

                          <HeaderCOA 
                            coa_name={CoaEnum.INCOME_TAX}
                            coa_enum={CoaEnum.INCOME_TAX}
                            coa_level={CoaHeaderEnum.HEADER}
                            label="NET PROFIT"
                            balance={totalNetProfit()?.netProfit}
                            header_level={CoaHeaderEnum.HEADER}
                            summary={totalNetProfit()?.summary}
                            average={totalNetProfit()?.average}
                          />
                        </>
                      )
                  }
                </tbody>
              </BaseTable>
            </div>
          </Card.Body>
        </Card>

        <FooterMenu
          export={{
            actiontype: "export",
            onClick: handleExport
          }}
          pdf={{
            actiontype: "pdf",
            onClick: () => exportPdf.handleExportPdf({
              date: methods.getValues().date,
              endDate: methods.getValues().dateTo,
              displayAccountNumber: false
            })
          }}
        />
      </section>
    </Fragment>
  )
}